import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Download, FileText, IdCard } from "lucide-react";
import { api, apiBlob, saveBlob } from "@/lib/api-client";
import { todayIso } from "@/lib/format";
import { useAppStore } from "@/store/app-store";
import { Button } from "@/components/ui/Button";
import { DateInput } from "@/components/ui/DateInput";
import { StateView } from "@/components/ui/StateView";

type RegisterRow = {
  sNo: number; arrival: string; departure: string; bookingRef: string; rooms: string; name: string; relationName: string; age: string; gender: string;
  nationality: string; address: string; phone: string; idType: string; idNumber: string; passport: string; visa: string;
  comingFrom: string; goingTo: string; purpose: string; child: boolean;
};

// Guest (police) register: everyone who checked in during the period - on screen, as Excel (CSV) or PDF.
export function PoliceRegisterPage() {
  const propertyId = useAppStore((state) => state.activeProperty!.id);
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(todayIso());
  const valid = Boolean(from && to && from <= to);
  const base = `/reports/police-register?propertyId=${propertyId}&from=${from}&to=${to}`;
  const query = useQuery({
    queryKey: ["bookings", propertyId, "police-register", from, to],
    queryFn: () => api<{ property: string; rows: RegisterRow[] }>(base),
    enabled: valid,
  });
  const download = useMutation({
    mutationFn: async (format: "csv" | "pdf") => saveBlob(await apiBlob(`${base}&format=${format}`), `guest-register-${from}-to-${to}.${format}`),
  });
  const rows = query.data?.rows ?? [];

  return (
    <main className="page register-page">
      <header className="page-head resource-head">
        <div>
          <h1>Police register</h1>
          <p className="page-head__meta">Guests who checked in during the period</p>
        </div>
      </header>
      <section className="card activity-filters">
        <label>From<DateInput max={to} value={from} onChange={(event) => setFrom(event.target.value)} /></label>
        <label>To<DateInput min={from} max={todayIso()} value={to} onChange={(event) => setTo(event.target.value)} /></label>
        <div className="register-page__downloads">
          <Button variant="secondary" disabled={!valid} loading={download.isPending && download.variables === "csv"} onClick={() => download.mutate("csv")}><Download size={16} /> Excel</Button>
          <Button variant="secondary" disabled={!valid} loading={download.isPending && download.variables === "pdf"} onClick={() => download.mutate("pdf")}><FileText size={16} /> PDF</Button>
        </div>
      </section>
      {download.error && <div className="form-error">{download.error.message}</div>}
      {!valid && <div className="form-error">Pick a valid date range.</div>}
      {query.isError && <StateView title="Couldn't load the register" message={query.error.message} action={() => void query.refetch()} />}
      {query.isLoading && <div className="form-hint">Loading...</div>}
      {query.data && !rows.length && <div className="card"><div className="mini-empty"><IdCard /><p>No guests checked in during this period.</p></div></div>}
      {rows.length > 0 && (
        <section className="card table-wrap">
          <table className="data-table register-table">
            <thead>
              <tr><th>#</th><th>Arrival</th><th>Guest</th><th>Age / gender</th><th>Nationality</th><th>Address</th><th>Mobile</th><th>ID</th><th>From → To</th><th>Purpose</th><th>Room</th><th>Departure</th></tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.bookingRef}-${row.sNo}`}>
                  <td className="num">{row.sNo}</td>
                  <td className="num">{row.arrival}</td>
                  <td><strong>{row.name}</strong>{row.relationName && <small>S/o, W/o: {row.relationName}</small>}{row.child && <small>Child</small>}<small>{row.bookingRef}</small></td>
                  <td>{[row.age, row.gender.toLowerCase()].filter(Boolean).join(" / ")}</td>
                  <td>{row.nationality}</td>
                  <td>{row.address}</td>
                  <td className="num">{row.phone}</td>
                  <td>{[row.idType, row.idNumber].filter(Boolean).join(" ")}{row.passport && <small>Passport {row.passport}</small>}{row.visa && <small>Visa {row.visa}</small>}</td>
                  <td>{[row.comingFrom, row.goingTo].filter(Boolean).join(" → ")}</td>
                  <td>{row.purpose}</td>
                  <td>{row.rooms}</td>
                  <td className="num">{row.departure}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </main>
  );
}
